import { fromUnixTime } from 'date-fns';
import type { ReactNode } from 'react';
import { useMemo } from 'react';

import { OrgStatBadge } from 'components/OrgStatBadge/OrgStatBadge';
import { BodyMicro } from 'components/Text/BodyMicro';
import { useUser } from 'contexts/UserEnvironmentContext';
import { useI18n } from 'i18n/hooks/useI18n';
import type { SearchAlgoliaOrgHit } from 'modules/search/algolia/types/SearchAlgoliaOrgHit';
import { SearchHitImage } from 'modules/search/components/Hit/Image/SearchHitImage';
import { SearchHitFloatingSaveButton } from 'modules/search/components/Hit/SaveButton/SearchHitFloatingSaveButton';
import {
  SearchHitCard,
  SearchHitHolder,
  SearchHitHoverEffect,
  SearchHitImageHolder,
} from 'modules/search/components/Hit/SearchHit.styled';
import { SearchHitInteractiveContentWrapper } from 'modules/search/components/Hit/SearchHitInteractiveContentWrapper';
import type { SearchContextHeaderLocationState } from 'modules/search/containers/SearchContextHeader/SearchContextHeaderLocationState';
import type { SearchSavedListingsStore } from 'modules/search/zustand-stores/searchSavedListingsStore';
import { getCityStateString } from 'utils/address/getCityStateString';
import { AREAS_OF_FOCUS } from 'utils/constants/general/areasOfFocus';
import { distanceInWordsToNow } from 'utils/date';
import { shuffleArray } from 'utils/functional/array';

import { SearchOrgHitInfo } from './SearchOrgHitInfo';

type Props = {
  hit: SearchAlgoliaOrgHit;
  href: string;
  trackClick: (() => void) | null;
  showLabel: boolean;
  searchSavedListingsStore: SearchSavedListingsStore | null;
  hideOwnerBanner: boolean;
  highlighted: boolean;
};

export function SearchOrgHit({
  hit,
  href,
  trackClick,
  showLabel,
  searchSavedListingsStore,
  hideOwnerBanner,
  highlighted,
}: Props) {
  const { i18n } = useI18n();

  const { user } = useUser();
  const {
    objectID,
    type,
    name,
    orgType,
    published,
    city,
    state,
    country,
    logo,
    hiring,
    seekingVolunteers,
    hasUpcomingEvents,
    areasOfFocus,
  } = hit;

  const location = useMemo(
    () =>
      getCityStateString(i18n, {
        city,
        stateCode: state,
        country,
      }),
    [i18n, city, country, state],
  );

  const pillItems: string[] = useMemo(() => {
    if (type === 'ORG' && areasOfFocus) {
      let trimmedAreasOfFocus = areasOfFocus;
      if (areasOfFocus.length > 3) {
        trimmedAreasOfFocus = shuffleArray(areasOfFocus).slice(0, 3);
      }

      return trimmedAreasOfFocus.map(
        (areaOfFocus) => AREAS_OF_FOCUS(i18n)[areaOfFocus],
      );
    }
    return [];
  }, [i18n, areasOfFocus, type]);

  const orgStat = useMemo(() => {
    if (hiring) {
      return (
        <OrgStatBadge iconName="star-check" text={i18n('Actively Hiring')} />
      );
    }
    if (hasUpcomingEvents) {
      return (
        <OrgStatBadge iconName="calendar" text={i18n('Upcoming Events')} />
      );
    }
    if (seekingVolunteers) {
      return (
        <OrgStatBadge iconName="group" text={i18n('Seeking Volunteers')} />
      );
    }
    return null;
  }, [i18n, hasUpcomingEvents, hiring, seekingVolunteers]);

  const listingStats = useMemo(() => {
    const stats: ReactNode[] = [];

    if (published) {
      stats.push(
        <BodyMicro lineHeight={1}>
          {i18n('Posted %(postedAt)s', {
            postedAt: distanceInWordsToNow(fromUnixTime(published)),
          })}
        </BodyMicro>,
      );
    }

    return stats;
  }, [i18n, published]);

  const ownerOrg = user ? user.orgs.find((o) => o.id === objectID) : null;

  return (
    <SearchHitHolder data-qa-id="search-result" data-hit-id={objectID}>
      <SearchHitCard>
        <SearchHitInteractiveContentWrapper
          roundedTop={hideOwnerBanner ?? !ownerOrg}
          highlighted={Boolean(highlighted)}
          listingLinkData={{
            type: 'link-with-state',
            to: href,
            state: {
              showSearchContext: true,
            } satisfies SearchContextHeaderLocationState,
          }}
          onClick={() => trackClick?.()}
        >
          <SearchHitHoverEffect />

          <SearchHitImageHolder $hiddenOnMobile={!logo}>
            <SearchHitImage
              isImported={false}
              imageUrl={null}
              image={null}
              source={null}
              name={name}
              logo={logo}
            />
          </SearchHitImageHolder>

          <SearchOrgHitInfo
            hiring={hiring}
            location={location}
            listingStats={listingStats}
            orgStat={orgStat}
            name={name}
            orgType={orgType}
            showLabel={showLabel}
            pillItems={pillItems}
          />
        </SearchHitInteractiveContentWrapper>

        {searchSavedListingsStore && (
          <SearchHitFloatingSaveButton
            hit={hit}
            searchSavedListingsStore={searchSavedListingsStore}
          />
        )}
      </SearchHitCard>
    </SearchHitHolder>
  );
}
